// Canadian statutory holidays used to defer birthdays / work-anniversaries
// on the Dashboard "Current Staff Birthdays and Work Anniversaries" banner.
// UnitedCloud is HQ'd in Calgary, so this covers the federal set + Alberta
// provincial holidays. When an occurrence lands on a weekend or one of
// these days it's rolled forward to the next business day.
//
// Kept intentionally lightweight — a full ical would balloon the bundle
// and this only needs to cover a rolling 12-month window at most.

// Ordinal-day-of-month helper: e.g. nthWeekdayOfMonth(2026, 1, 1, 3) →
// 3rd Monday of February 2026 (Alberta Family Day). weekday: 0=Sun … 6=Sat.
function nthWeekdayOfMonth(year, monthIndex, weekday, nth) {
  const first = new Date(year, monthIndex, 1);
  const offset = (7 + weekday - first.getDay()) % 7;
  return new Date(year, monthIndex, 1 + offset + (nth - 1) * 7);
}

// Monday preceding May 25 (Victoria Day).
function mondayBeforeMay25(year) {
  const may25 = new Date(year, 4, 25);
  const back = (may25.getDay() + 6) % 7; // days back to Monday
  return new Date(year, 4, 25 - back);
}

// Good Friday via Meeus/Butcher (Gregorian).
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=March, 4=April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function goodFriday(year) {
  const e = easterSunday(year);
  return new Date(e.getFullYear(), e.getMonth(), e.getDate() - 2);
}

// Roll forward when the fixed date lands on Sat/Sun (federal observance).
function observedFixed(date) {
  const d = date.getDay();
  if (d === 6) return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 2);
  if (d === 0) return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  return date;
}

// Build the holiday set for a given year (federal + Alberta).
export function canadaHolidaysForYear(year) {
  const list = [
    observedFixed(new Date(year, 0, 1)),                // New Year's Day
    nthWeekdayOfMonth(year, 1, 1, 3),                    // Family Day (AB, 3rd Mon Feb)
    goodFriday(year),                                    // Good Friday
    mondayBeforeMay25(year),                             // Victoria Day
    observedFixed(new Date(year, 6, 1)),                // Canada Day (Jul 1)
    nthWeekdayOfMonth(year, 7, 1, 1),                    // Alberta Heritage Day (1st Mon Aug)
    nthWeekdayOfMonth(year, 8, 1, 1),                    // Labour Day (1st Mon Sept)
    observedFixed(new Date(year, 8, 30)),               // Nat'l Day for Truth & Reconciliation (Sept 30)
    nthWeekdayOfMonth(year, 9, 1, 2),                    // Thanksgiving (2nd Mon Oct)
    observedFixed(new Date(year, 10, 11)),              // Remembrance Day (Nov 11)
    observedFixed(new Date(year, 11, 25)),              // Christmas Day (Dec 25)
    observedFixed(new Date(year, 11, 26)),              // Boxing Day (Dec 26)
  ];
  return list.map((d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
}

// Fast lookup set covering [year-1, year, year+1] so a December date
// can roll into January of the next year without falling off the end.
export function buildHolidaySet(refDate = new Date()) {
  const y = refDate.getFullYear();
  const s = new Set();
  for (const yr of [y - 1, y, y + 1]) {
    for (const iso of canadaHolidaysForYear(yr)) s.add(iso);
  }
  return s;
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Roll a date forward to the next business day (Mon-Fri and not a
// Canadian statutory holiday). Returns the same date if already valid.
export function nextBusinessDay(date, holidaySet) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const hs = holidaySet || buildHolidaySet(d);
  // Cap at 10 iterations to guard against pathological data.
  for (let i = 0; i < 10; i++) {
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6 && !hs.has(isoDate(d))) return d;
    d.setDate(d.getDate() + 1);
  }
  return d;
}
